/**
 * Variant tier inheritance helper.
 *
 * Variable products have a tier inheritance model:
 *   - Product-level tiers (variant_id = NULL) are the "default" rates that
 *     apply to ALL variants out of the box.
 *   - A specific variant may override those by providing its own tiers
 *     (variant_id = that variant's id). When it does, its override REPLACES
 *     the default — they are not merged. Match the admin's tier-vs-default
 *     copy.
 *
 * `tiersForVariant()` returns the tiers that should be used for the given
 * variant: its own overrides if any exist, otherwise the product-level
 * defaults. Callers can hand the result to display logic (table) or pricing
 * logic (resolve unit price for current qty).
 */

export type TierLike = {
  variant_id: string | null;
  min_qty: number;
  max_qty: number | null;
  unit_price: number;
};

export function tiersForVariant<T extends TierLike>(
  tiers: T[],
  variantId: string | null | undefined,
): T[] {
  // A tier priced 0 was never configured — the same rule as a variant priced
  // 0. Left in, it advertised 0.00 rows and could price a line at nothing.
  const usable = tiers.filter((t) => Number(t.unit_price) > 0);
  if (variantId) {
    const overrides = usable.filter((t) => t.variant_id === variantId);
    if (overrides.length > 0) return overrides;
  }
  return usable.filter((t) => t.variant_id === null);
}

/**
 * The tier that applies at a given quantity, honouring variant inheritance.
 * Highest matching `min_qty` wins. Null when no tier covers the quantity.
 *
 * Every surface that prices a tier needs this, and each having its own copy is
 * how the PDP, the cart and the add-to-cart block drifted apart in the first
 * place.
 */
export function matchTierForQty<T extends TierLike>(
  tiers: T[],
  qty: number,
  variantId?: string | null,
): T | null {
  return (
    tiersForVariant(tiers, variantId)
      .filter((t) => qty >= t.min_qty && (t.max_qty === null || qty <= t.max_qty))
      .sort((a, b) => b.min_qty - a.min_qty)[0] ?? null
  );
}

/**
 * Stack an active campaign's discount onto an already-resolved tier price.
 * A ratio outside (0, 1) means there is no usable campaign — return the tier
 * untouched rather than invent a price.
 */
export function applySaleRatio(
  tierPrice: number,
  ratio: number | null | undefined,
): number {
  if (ratio == null || ratio <= 0 || ratio >= 1) return tierPrice;
  return Math.round(tierPrice * ratio * 100) / 100;
}

export type SaleLike = { sale_price: number; original_price: number } | null;

/**
 * Decide the unit price to advertise and the figure to strike through.
 *
 * Tier and campaign COMPOUND: a matched tier sets the unit price for the chosen
 * quantity, then the campaign's discount comes off that, and the struck figure
 * is the tier price the customer would have paid without the campaign.
 *
 * With no tier matched the campaign's own numbers are exact, so they are used
 * as-is — deriving a ratio there would drift whenever the list price moved
 * after the campaign snapshot was taken.
 */
export function resolveDisplayPricing(args: {
  tierPrice: number;
  tierMatched: boolean;
  sale: SaleLike;
  /**
   * The price with no tier applied. When given (and above 0), a tier can only
   * lower it. Pass it only for a shopper in a price group — see
   * `composeUnitPrice`.
   */
  untieredPrice?: number;
}): { displayPrice: number; compareAtPrice: number | null } {
  const { tierPrice, tierMatched, sale, untieredPrice } = args;
  if (tierMatched && untieredPrice != null && untieredPrice > 0) {
    const tiered = resolveDisplayPricing({ tierPrice, tierMatched, sale });
    const untiered = resolveDisplayPricing({
      tierPrice: untieredPrice,
      tierMatched: false,
      sale,
    });
    return tiered.displayPrice < untiered.displayPrice ? tiered : untiered;
  }
  if (!sale) return { displayPrice: tierPrice, compareAtPrice: null };
  if (!tierMatched) {
    return {
      displayPrice: sale.sale_price,
      compareAtPrice: sale.original_price,
    };
  }
  const ratio =
    sale.original_price > 0 ? sale.sale_price / sale.original_price : null;
  return {
    displayPrice: applySaleRatio(tierPrice, ratio),
    compareAtPrice: tierPrice,
  };
}

// ─── Price groups (wholesale/VIP) ───────────────────────────────────
//
// For a shopper in a price group, the group price replaces the base price; a
// campaign applies only when it is cheaper still; a tier can only lower the
// group price. Checkout, cart and PDP all follow these rules.

/**
 * The unit price a line costs before add-ons — the order route's arithmetic.
 * `tier` is whatever `resolve_tier_price` returned (it returns the base price
 * when no tier matched, which the "only lowers" rule then ignores).
 */
export function composeUnitPrice(args: {
  base: number;
  group?: number | null;
  sale?: number | null;
  tier?: number | null;
}): number {
  const own = args.group ?? args.base;
  const sale = args.sale != null && args.sale < own ? args.sale : null;
  const untiered = sale ?? own;
  if (args.tier == null) return untiered;
  const ratio = sale != null && own > 0 ? sale / own : null;
  const tiered = applySaleRatio(args.tier, ratio);
  // Only a group price is protected from a tier — a default tier can sit above
  // what the group pays. Everyone else pays the tier, even one above the base
  // price: stores that enter their cheapest break as the base price have every
  // tier above it. A product with no usable price (0) is priced by its tiers.
  return args.group != null && untiered > 0 ? Math.min(untiered, tiered) : tiered;
}

/**
 * A campaign as a group shopper sees it: gone when it isn't below their group
 * price, and struck through against the group price rather than the base.
 */
export function groupAdjustedSale<T extends { sale_price: number; original_price: number }>(
  sale: T | null,
  groupPrice: number | null | undefined,
): T | null {
  if (!sale || groupPrice == null) return sale;
  if (sale.sale_price >= groupPrice) return null;
  return { ...sale, original_price: groupPrice };
}

/**
 * Reduce tiers to the ones this shopper's group uses, per variant scope —
 * the group's own tiers where it has any, the defaults otherwise — mirroring
 * `resolve_tier_price`. Another group's tiers never survive.
 */
export function scopeTiersToGroup<T extends TierLike & { price_group_id: string | null }>(
  tiers: T[],
  groupId: string | null,
): T[] {
  const groupScopes = new Set(
    groupId
      ? tiers.filter((t) => t.price_group_id === groupId).map((t) => t.variant_id)
      : [],
  );
  return tiers.filter((t) =>
    groupScopes.has(t.variant_id)
      ? t.price_group_id === groupId
      : t.price_group_id === null,
  );
}
